import React, { memo, useState } from 'react';
import { Modal, Button, TextInput, Textarea, Select } from '@mantine/core';
import { useForm } from '@mantine/form';
import { useSupportStyles } from './modal-style';
import { notifications } from '@mantine/notifications';
import { BiCheck } from 'react-icons/bi';
import { MdClose } from 'react-icons/md';
import { getHeader } from '../../config/userSession';
import { IconCheck, IconX } from '@tabler/icons-react';

export const SupportModal = memo(({ opened, onClose, email, reload }) => {
  const { classes, theme } = useSupportStyles();
  const [loading, setLoading] = useState(false);

  const form = useForm({
    initialValues: {
      subject: '',
      issueType: '',
      description: '',
      contactEmail: email
    },
    validate: {
      subject: (value) =>
        value.trim().length < 3
          ? 'Subject must be at least 3 characters'
          : null,
      issueType: (value) => (!value ? 'Please select an issue type' : null),
      description: (value) =>
        value.trim().length < 10
          ? 'Description must be at least 10 characters'
          : null,
      contactEmail: (value) =>
        !/^\S+@\S+$/.test(value) ? 'Invalid email address' : null
    }
  });
  const showNotification = (notificationType, message, title, width) => {
    let colorCode = notificationType === 'success' ? 'secondary.0' : 'red';

    notifications.show({
      id: 'support-notification',
      withCloseButton: true,
      title: title,
      message: message,
      color: colorCode,
      icon:
        notificationType === 'success' ? (
          <IconCheck size="2.5rem" />
        ) : (
          <IconX size="2.5rem" />
        ),
      style: {
        backgroundColor: theme.colors.background[0],
        padding: '12px 12px',
        fontSize: '8rem',
        lineHeight: '1.2',
        minHeight: '120px',
        width: width, // 👈 Set desired width here
        maxWidth: '90%' // Optional: responsive on smaller screens
      },
      styles: {
        title: {
          fontSize: '1.5rem',
          lineHeight: 1.3
        },
        description: {
          fontSize: '1.2rem',
          lineHeight: 1.4
        },
        closeButton: {
          color: 'white',
          backgroundColor: 'black',
          '&:hover': {
            backgroundColor: 'gray', // Change to your preferred hover color
            color: 'yellow' // Example hover text color
          }
        }
      }
    });
  };
  const handleSubmit = async (values) => {
    setLoading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/account/send-support-email`,
        {
          method: 'POST',
          headers: getHeader(),
          body: JSON.stringify(values)
        }
      );

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || 'Failed to send email');
      }
      reload();
      showNotification(
        'success',
        'Your support request has been sent successfully',
        '',
        '550px'
      );

      form.reset();
      onClose();
    } catch (error) {
      showNotification('failure', 'Something went wrong', '', '250px');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={<span className={classes.title}>Report an Issue</span>}
      size="xl"
    >
      <div className={classes.wrapper}>
        <form onSubmit={form.onSubmit(handleSubmit)}>
          <div className={classes.inputFields}>
            <div className={classes.inputWrapper}>
              <Select
                required
                label="Issue Type"
                placeholder="Select the type of issue"
                classNames={{
                  root: classes.textInput,
                  label: classes.label,
                  error: classes.error
                }}
                data={[
                  { value: 'technical', label: 'Technical Problem' },
                  { value: 'access', label: 'Access Issues' },
                  { value: 'other', label: 'Other' }
                ]}
                {...form.getInputProps('issueType')}
              />
            </div>

            <div className={classes.inputWrapper}>
              <TextInput
                required
                label="Subject"
                placeholder="Brief summary of the issue"
                classNames={{
                  root: classes.textInput,
                  label: classes.label,
                  error: classes.error
                }}
                {...form.getInputProps('subject')}
              />
            </div>

            <div className={classes.inputWrapper} style={{ width: '100%' }}>
              <label className={classes.label}>Description</label>
              <Textarea
                required
                placeholder="Please provide detailed information about the issue"
                minRows={4}
                classNames={{
                  input: classes.textareaInput,
                  error: classes.error
                }}
                {...form.getInputProps('description')}
              />
            </div>
          </div>

          <Button
            type="submit"
            loading={loading}
            className={classes.submitButton}
            fullWidth
            mt="xl"
          >
            Submit Report
          </Button>
        </form>
      </div>
    </Modal>
  );
});
